// Level definitions, themes, palette, dimensions, stage progression.
// All layout/generation logic lives here. Physics and rendering import
// constants + THEMES from this module.

export const TILE = 40;
export const COLS = 24;
export const ROWS = 16;
export const W = COLS * TILE; // 960
export const H = ROWS * TILE; // 640

// Colors from design guidelines
export const COLORS = {
  bg: "#09090b",
  asphalt: "#111114",
  grid: "rgba(0,240,255,0.06)",
  line: "rgba(255,255,255,0.08)",
  parked: "#2a2a2f",
  parkedEdge: "#3a3a42",
  player: "#00F0FF",
  playerGlow: "rgba(0,240,255,0.6)",
  enemy: "#FF0055",
  enemyGlow: "rgba(255,0,85,0.55)",
  exit: "#39FF14",
  exitGlow: "rgba(57,255,20,0.55)",
  power: "#FFD400",
  powerGlow: "rgba(255,212,0,0.6)",
  wall: "#1a1a1f",
  wallEdge: "#2a2a30",
  text: "#f8f9fa",
};

// Random utilities
const rand = (a, b) => a + Math.random() * (b - a);
const irand = (a, b) => Math.floor(rand(a, b + 1));

// Simple AABB intersection (shared by physics + rendering hit tests)
export function aabb(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// Palette shared by all lot-style levels
const carColors = ["#3a4b6b", "#5a3b3b", "#3a5a4b", "#4a3a6b", "#5b5b3a", "#3d3d45", "#6b3a5a", "#4a5b3a"];
function pickCarColor() {
  return carColors[irand(0, carColors.length - 1)];
}

// Stage progression: every SCORE_PER_STAGE points, difficulty ramps up.
export const SCORE_PER_STAGE = 500;
export function stageFromScore(score) {
  return 1 + Math.floor(score / SCORE_PER_STAGE);
}

// Build a level: returns { walls, obstacles, exits, playerStart, enemySpawns, [laneCenters] }
export function buildLevel(level) {
  const walls = []; // outer/boundary walls (impassable)
  const obstacles = []; // parked cars
  const exits = [];

  const B = 20; // border thickness

  // ===================== TUNNELS LEVEL =====================
  // Grid of narrow horizontal + vertical corridors. Cars chase you around
  // tight corners. Two exits at opposite corners of the map.
  if (level === "tunnels") {
    exits.push({ x: W - B, y: 60, w: B, h: 60 });         // right top
    exits.push({ x: 20, y: H - B, w: 80, h: B });          // bottom left
    walls.push({ x: 0, y: 0, w: W, h: B });                // top full
    walls.push({ x: 0, y: 0, w: B, h: H });                // left full
    walls.push({ x: W - B, y: 0, w: B, h: 60 });           // right upper (above exit)
    walls.push({ x: W - B, y: 120, w: B, h: H - 120 });    // right below exit
    walls.push({ x: 100, y: H - B, w: W - 100, h: B });    // bottom (after exit)

    // Interior maze walls — horizontal corridors
    const rowYs = [110, 200, 290, 380, 470];
    const colXs = [140, 300, 460, 620, 780];
    for (const y of rowYs) {
      for (let i = 0; i < colXs.length; i++) {
        const x0 = i === 0 ? B : colXs[i - 1] + 40;
        const x1 = colXs[i];
        if (x1 - x0 > 20) walls.push({ x: x0, y, w: x1 - x0, h: 10 });
      }
      const last = colXs[colXs.length - 1] + 40;
      if (W - B - last > 20) walls.push({ x: last, y, w: W - B - last, h: 10 });
    }
    for (const x of colXs) {
      for (let i = 0; i < rowYs.length - 1; i += 2) {
        walls.push({ x, y: rowYs[i] + 10, w: 10, h: rowYs[i + 1] - rowYs[i] - 10 });
      }
    }

    const playerStart = { x: 40, y: H - 40 };
    const enemySpawns = [
      { x: W - 60, y: 40 },
      { x: 40, y: 40 },
      { x: W / 2, y: 40 },
      { x: W - 60, y: H - 40 },
    ];
    return { walls, obstacles, exits, playerStart, enemySpawns };
  }

  // ===================== HIGHWAY LEVEL =====================
  if (level === "highway") {
    walls.push({ x: 0, y: 0, w: B, h: H });                // left
    walls.push({ x: W - B, y: 0, w: B, h: H });            // right
    walls.push({ x: 0, y: H - B, w: W, h: B });            // bottom (no exit here)
    exits.push({ x: W / 2 - 45, y: 0, w: 90, h: B });
    walls.push({ x: 0, y: 0, w: W / 2 - 45, h: B });
    walls.push({ x: W / 2 + 45, y: 0, w: W / 2 - 45, h: B });

    const laneCount = 6;
    const laneAreaY0 = 70;
    const laneAreaY1 = H - 90;
    const laneSpan = (laneAreaY1 - laneAreaY0) / laneCount;
    const laneCenters = [];
    for (let i = 0; i < laneCount; i++) laneCenters.push(laneAreaY0 + laneSpan * (i + 0.5));

    const enemySpawns = [];
    for (let i = 0; i < laneCount; i++) {
      const dir = i % 2 === 0 ? 1 : -1;
      enemySpawns.push({ x: dir === 1 ? -30 : W + 30, y: laneCenters[i], lane: i, dir });
    }

    const playerStart = { x: W / 2 - 7, y: H - 42 };
    return { walls, obstacles, exits, playerStart, enemySpawns, laneCenters };
  }

  // Snowstorm / beach layouts mirror outdoor (2 exits) — challenge is friction/sand.
  const isSnow = level === "snowstorm";
  const isNeonRain = level === "neon_rain";
  const isBeach = level === "beach";

  // Outdoor-like layouts share the same 2-exit open lot skeleton.
  const isOutdoorLike =
    level === "outdoor" || level === "rooftop" || isSnow || isNeonRain || isBeach;

  if (isOutdoorLike) {
    exits.push({ x: W - B, y: H / 2 - 40, w: B, h: 80 });      // right middle
    exits.push({ x: W / 2 - 40, y: H - B, w: 80, h: B });      // bottom middle
  } else {
    exits.push({ x: W / 2 - 30, y: 0, w: 60, h: B });          // top center only
  }

  const pushSeg = (x, y, w, h) => {
    if (w > 0 && h > 0) walls.push({ x, y, w, h });
  };

  // Build boundary walls with gaps for each exit
  // TOP wall
  {
    const topExits = exits.filter((e) => e.y === 0).sort((a, b) => a.x - b.x);
    let cursor = 0;
    for (const e of topExits) {
      pushSeg(cursor, 0, e.x - cursor, B);
      cursor = e.x + e.w;
    }
    pushSeg(cursor, 0, W - cursor, B);
  }
  // BOTTOM wall
  {
    const botExits = exits.filter((e) => e.y + e.h === H).sort((a, b) => a.x - b.x);
    let cursor = 0;
    for (const e of botExits) {
      pushSeg(cursor, H - B, e.x - cursor, B);
      cursor = e.x + e.w;
    }
    pushSeg(cursor, H - B, W - cursor, B);
  }
  // LEFT wall
  {
    const leftExits = exits.filter((e) => e.x === 0).sort((a, b) => a.y - b.y);
    let cursor = 0;
    for (const e of leftExits) {
      pushSeg(0, cursor, B, e.y - cursor);
      cursor = e.y + e.h;
    }
    pushSeg(0, cursor, B, H - cursor);
  }
  // RIGHT wall
  {
    const rightExits = exits.filter((e) => e.x + e.w === W).sort((a, b) => a.y - b.y);
    let cursor = 0;
    for (const e of rightExits) {
      pushSeg(W - B, cursor, B, e.y - cursor);
      cursor = e.y + e.h;
    }
    pushSeg(W - B, cursor, B, H - cursor);
  }

  // Underground extra concrete pillars (kept clear of exit approach)
  if (level === "underground") {
    for (let i = 0; i < 5; i++) {
      walls.push({ x: 140 + i * 150, y: 80, w: 22, h: 22 });
      walls.push({ x: 140 + i * 150, y: H - 102, w: 22, h: 22 });
    }
  }

  // Beach: scatter palm-tree obstacles (rendered as circular trunks) around the lot
  if (isBeach) {
    // Place 8 palm trees at pseudo-random but clear-of-spawn positions.
    // We model them as ~26x26 impassable square colliders (trunk + fronds).
    const palms = [
      { x: 220, y: 90 }, { x: 460, y: 130 }, { x: 720, y: 90 },
      { x: 160, y: 300 }, { x: 780, y: 300 },
      { x: 260, y: 500 }, { x: 520, y: 520 }, { x: 720, y: 500 },
    ];
    for (const t of palms) {
      walls.push({ x: t.x, y: t.y, w: 26, h: 26, palm: true });
    }
  }

  // VERTICAL parked cars in bays (back-to-back), with drivable aisles.
  const carW = 30;
  const carH = 50;
  const bayInnerGap = 22;                       // back-to-back cars in same bay
  const bayWidth = carW * 2 + bayInnerGap;      // 82
  const aisleWidth = isOutdoorLike ? 68 : 64;
  const sectionWidth = bayWidth + aisleWidth;
  const startX = 40;
  const bays = Math.floor((W - startX * 2) / sectionWidth);
  const carsPerCol = isOutdoorLike ? 6 : 7;
  const rowStart = 60;
  const rowPitch = isOutdoorLike ? 84 : 78;
  // Beach lot is sparser (more sand) to make room for palms
  const skipProb = isBeach ? 0.5 : (isOutdoorLike ? 0.68 : 0.78);

  for (let b = 0; b < bays; b++) {
    const bayX = startX + b * sectionWidth;
    for (let r = 0; r < carsPerCol; r++) {
      const y = rowStart + r * rowPitch;
      if (y + carH > H - B - 10) continue;
      if (Math.random() < skipProb) {
        obstacles.push({ x: bayX, y, w: carW, h: carH, color: pickCarColor(), facing: "left" });
      }
      if (Math.random() < skipProb) {
        obstacles.push({ x: bayX + carW + bayInnerGap, y, w: carW, h: carH, color: pickCarColor(), facing: "right" });
      }
    }
  }

  // Ensure spawn area on left is clear
  const playerStart = { x: 30, y: H / 2 };
  const clearZone = { x: 22, y: playerStart.y - 50, w: 60, h: 100 };
  for (let i = obstacles.length - 1; i >= 0; i--) {
    if (aabb(obstacles[i], clearZone)) obstacles.splice(i, 1);
  }

  // Enemy spawns in aisles
  const enemySpawns = [];
  for (let b = 0; b <= bays; b++) {
    const aisleCenter = startX + b * sectionWidth - aisleWidth / 2 + 4;
    if (aisleCenter > 30 && aisleCenter < W - 30) {
      enemySpawns.push({ x: aisleCenter, y: 45 });
      enemySpawns.push({ x: aisleCenter, y: H - 45 });
    }
  }
  if (enemySpawns.length === 0) {
    enemySpawns.push({ x: W - 60, y: 60 });
    enemySpawns.push({ x: 60, y: H - 60 });
  }

  return { walls, obstacles, exits, playerStart, enemySpawns };
}

// Theme per level: colors, lighting behaviour
export const THEMES = {
  outdoor: {
    sky: "#c9d9e0",
    asphalt: "#3a3f47",
    asphaltDark: "#2a2e35",
    lane: "rgba(255,255,255,0.14)",
    ambient: 0.9,
    shadowColor: "rgba(0,0,0,0.35)",
    shadowOffset: { x: 6, y: 8 },
    lensFlare: true,
    sunColor: "rgba(255,220,140,0.18)",
  },
  underground: {
    sky: "#050508",
    asphalt: "#141418",
    asphaltDark: "#0b0b0f",
    lane: "rgba(255,255,255,0.05)",
    ambient: 0.35,
    shadowColor: "rgba(0,0,0,0.55)",
    shadowOffset: { x: 2, y: 3 },
    lensFlare: false,
    flashlightRadius: 170,
    ceilingLights: [
      { x: W * 0.2, y: 40 },
      { x: W * 0.5, y: 40 },
      { x: W * 0.8, y: 40 },
      { x: W * 0.2, y: H - 40 },
      { x: W * 0.5, y: H - 40 },
      { x: W * 0.8, y: H - 40 },
    ],
  },
  rooftop: {
    sky: "#f2794b",
    asphalt: "#2c1e1e",
    asphaltDark: "#1c1414",
    lane: "rgba(255,180,120,0.16)",
    ambient: 0.9,
    shadowColor: "rgba(60,20,20,0.4)",
    shadowOffset: { x: 8, y: 6 },
    lensFlare: true,
    sunColor: "rgba(255,150,60,0.28)",
  },
  highway: {
    sky: "#0a1224",
    asphalt: "#1a1d24",
    asphaltDark: "#101318",
    lane: "rgba(255,220,80,0.35)",
    ambient: 0.75,
    shadowColor: "rgba(0,0,0,0.4)",
    shadowOffset: { x: 3, y: 4 },
    lensFlare: false,
    sunColor: "rgba(200,220,255,0.12)",
  },
  snowstorm: {
    sky: "#dfe8f2",
    asphalt: "#6a7382",
    asphaltDark: "#525b6a",
    lane: "rgba(255,255,255,0.18)",
    ambient: 0.85,
    shadowColor: "rgba(40,60,90,0.3)",
    shadowOffset: { x: 4, y: 5 },
    lensFlare: false,
    sunColor: "rgba(220,235,255,0.08)",
  },
  tunnels: {
    sky: "#1a1d24",
    asphalt: "#252932",
    asphaltDark: "#1a1d24",
    lane: "rgba(200,220,255,0.06)",
    ambient: 0.55,
    shadowColor: "rgba(0,0,0,0.5)",
    shadowOffset: { x: 3, y: 4 },
    lensFlare: false,
    sunColor: "rgba(200,220,255,0.06)",
  },
  neon_rain: {
    sky: "#1a0a2e",
    asphalt: "#221534",
    asphaltDark: "#150a24",
    lane: "rgba(240,171,252,0.28)",
    ambient: 0.7,
    shadowColor: "rgba(30,10,45,0.55)",
    shadowOffset: { x: 4, y: 6 },
    lensFlare: false,
    sunColor: "rgba(240,171,252,0.15)",
    lamps: [
      { x: 140, y: 120, hue: "#F0ABFC" },
      { x: 820, y: 140, hue: "#00F0FF" },
      { x: 180, y: 520, hue: "#00F0FF" },
      { x: 780, y: 500, hue: "#F0ABFC" },
      { x: 480, y: 320, hue: "#F0ABFC" },
    ],
  },
  beach: {
    sky: "#fde8b8",              // warm golden-hour sky
    asphalt: "#e8c98a",           // sun-baked sand
    asphaltDark: "#c9a565",
    lane: "rgba(255,255,255,0.35)",
    ambient: 1.0,
    shadowColor: "rgba(120,80,30,0.35)",
    shadowOffset: { x: 6, y: 7 },
    lensFlare: true,
    sunColor: "rgba(255,200,120,0.22)",
  },
};
